import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { httpClient } from '../../utils/apiCall';
import { handleApiError } from '../../utils/apiError';
import { API_ENDPOINTS } from '../../config/api';
import { 
  QuoteDto, 
  QuoteItemDto, 
  AddQuoteFromPORequestDto,
  UpdateSingleQuoteItemDto,
  SetQuoteItemNegotiateDto,
  QuoteListParams,
  QuoteDetailParams,
  QuoteCloseCreateParams,
  QuoteSendMailCreateParams,
  QuoteItemSetAwardedPartialUpdateParams,
  QuoteItemSetRejectedPartialUpdateParams,
  QuoteSetClosedPartialUpdateParams,
  QuoteSetRespondedPartialUpdateParams,
  QuoteSetViewedPartialUpdateParams,
  QuotePublicDetailParams,
  UpdateQuoteDto,
  PoRequestQuoteItemsListParams,
  PoRequestSupplierAwardedQuoteItemsListParams,
  PoRequestSupplierNegotiationQuoteItemsListParams
} from '../../generated';

// Manual type definition for AddQuoteFromQuoteRequestDto (not yet generated)
export interface AddQuoteFromQuoteRequestDto {
  supplierId: number;
  supplierEmail?: string | null;
  timeoutDays: number;
  noteToSupplier?: string | null;
  quoteItemIds?: number[] | null;
  sendMailAutomaticaly: boolean;
}

// Manual type definition for QuoteItemHistoryDto (not yet generated)
export interface QuoteItemHistoryDto {
  poRequestId: number;
  sourceQuoteItemId: number;
  step: string | null;
  quantity: number | null;
  price: number | null;
  currency: string | null;
  deliveryDays: number | null;
  stageDate: string; // ISO date string
  userName?: string | null;
}

export interface SendQuoteMailFromPORequestDto {
  supplierEmail: string;
}

// State interface
export interface QuoteState {
  quotes: QuoteDto[];
  currentQuote: QuoteDto | null;
  quoteItems: QuoteItemDto[];
  poRequestQuoteItems: QuoteItemDto[];
  negotiationSuppliers: any[];
  awardedSuppliers: any[];
  supplierAwardedQuoteItems: QuoteItemDto[];
  supplierNegotiationQuoteItems: QuoteItemDto[];
  quoteItemHistory: QuoteItemHistoryDto[];
  loading: {
    list: boolean;
    detail: boolean;
    create: boolean;
    update: boolean;
    updateItems: boolean;
    delete: boolean;
    close: boolean;
    sendMail: boolean;
    fromPoRequests: boolean;
    fromQuoteItems: boolean;
    setAwarded: boolean;
    setRejected: boolean;
    setClosed: boolean;
    setResponded: boolean;
    setViewed: boolean;
    reviewDetail: boolean;
    reviewItemsUpdate: boolean;
    reviewUpdate: boolean;
    negotiate: boolean;
    rejectPublicItem: boolean;
    bulk: boolean;
    poRequestQuoteItems: boolean;
    negotiationSuppliers: boolean;
    awardedSuppliers: boolean;
    supplierAwardedQuoteItems: boolean;
    supplierNegotiationQuoteItems: boolean;
    quoteItemHistory: boolean;
    checkout: boolean;
  };
  error: {
    list: string | null;
    detail: string | null;
    create: string | null;
    update: string | null;
    updateItems: string | null;
    delete: string | null;
    close: string | null;
    sendMail: string | null;
    fromPoRequests: string | null;
    fromQuoteItems: string | null;
    setAwarded: string | null;
    setRejected: string | null;
    setClosed: string | null;
    setResponded: string | null;
    setViewed: string | null;
    reviewDetail: string | null;
    reviewItemsUpdate: string | null;
    reviewUpdate: string | null;
    negotiate: string | null;
    rejectPublicItem: string | null;
    bulk: string | null;
    poRequestQuoteItems: string | null;
    negotiationSuppliers: string | null;
    awardedSuppliers: string | null;
    supplierAwardedQuoteItems: string | null;
    supplierNegotiationQuoteItems: string | null;
    quoteItemHistory: string | null;
    checkout: string | null;
  };
  filters: QuoteListParams;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

// Initial state
const initialState: QuoteState = {
  quotes: [],
  currentQuote: null,
  quoteItems: [],
  poRequestQuoteItems: [],
  negotiationSuppliers: [],
  awardedSuppliers: [],
  supplierAwardedQuoteItems: [],
  supplierNegotiationQuoteItems: [],
  quoteItemHistory: [],
  loading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    updateItems: false,
    delete: false,
    close: false,
    sendMail: false,
    fromPoRequests: false,
    fromQuoteItems: false,
    setAwarded: false,
    setRejected: false,
    setClosed: false,
    setResponded: false,
    setViewed: false,
    reviewDetail: false,
    reviewItemsUpdate: false,
    reviewUpdate: false,
    negotiate: false,
    rejectPublicItem: false,
    bulk: false,
    poRequestQuoteItems: false,
    negotiationSuppliers: false,
    awardedSuppliers: false,
    supplierAwardedQuoteItems: false,
    supplierNegotiationQuoteItems: false,
    quoteItemHistory: false,
    checkout: false,
  },
  error: {
    list: null,
    detail: null,
    create: null,
    update: null,
    updateItems: null,
    delete: null,
    close: null,
    sendMail: null,
    fromPoRequests: null,
    fromQuoteItems: null,
    setAwarded: null,
    setRejected: null,
    setClosed: null,
    setResponded: null,
    setViewed: null,
    reviewDetail: null,
    reviewItemsUpdate: null,
    reviewUpdate: null,
    negotiate: null,
    rejectPublicItem: null,
    bulk: null,
    poRequestQuoteItems: null,
    negotiationSuppliers: null,
    awardedSuppliers: null,
    supplierAwardedQuoteItems: null,
    supplierNegotiationQuoteItems: null,
    quoteItemHistory: null,
    checkout: null,
  },
  filters: {},
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
};

// Hata çevirimi ortak yardımcıda: iptal edilen istek için null döner (hata sayılmaz).


// Async thunks
export const fetchQuotes = createAsyncThunk(
  'quote/fetchQuotes',
  async (params: QuoteListParams = {}, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryString.append(key, value.toString());
        }
      });
      const url = `${API_ENDPOINTS.QUOTE.LIST}${queryString.toString() ? `?${queryString.toString()}` : ''}`;
      const response = await httpClient.get(url);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to fetch quotes');
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchQuoteDetail = createAsyncThunk(
  'quote/fetchQuoteDetail',
  async (params: QuoteDetailParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.DETAIL}/${params.id}`;
      const response = await httpClient.get(url);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to fetch quote details');
      return rejectWithValue(errorMessage);
    }
  }
);

export const createQuote = createAsyncThunk(
  'quote/createQuote',
  async (quoteData: QuoteDto, { rejectWithValue }) => {
    try {
      const response = await httpClient.post(API_ENDPOINTS.QUOTE.CREATE, quoteData);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to create quote');
      return rejectWithValue(errorMessage);
    }
  }
);

export const createQuoteFromPoRequests = createAsyncThunk(
  'quote/createQuoteFromPoRequests',
  async (data: AddQuoteFromPORequestDto, { rejectWithValue }) => {
    try {
      const response = await httpClient.post(API_ENDPOINTS.QUOTE.FROM_PO_REQUESTS, data);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to create quote from PO requests');
      return rejectWithValue(errorMessage);
    }
  }
);

export const createQuoteFromQuoteItems = createAsyncThunk(
  'quote/createQuoteFromQuoteItems',
  async (data: AddQuoteFromQuoteRequestDto, { rejectWithValue }) => {
    try {
      const response = await httpClient.post(API_ENDPOINTS.QUOTE.FROM_QUOTE_ITEMS, data);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to create quote from quote items');
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateQuote = createAsyncThunk(
  'quote/updateQuote',
  async ({ data }: { id: number; data: Partial<QuoteDto> }, { rejectWithValue }) => {
    try {
      // Note: Update endpoint might not exist in the API, using create as fallback
      const response = await httpClient.post(API_ENDPOINTS.QUOTE.CREATE, data as QuoteDto);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to update quote');
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateQuoteItems = createAsyncThunk(
  'quote/updateQuoteItems',
  async ({ quoteId, data }: { quoteId: number; data: UpdateSingleQuoteItemDto }, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.UPDATE}/${quoteId}/items`;
      const response = await httpClient.patch(url, data);
      return { quoteId, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to update quote items');
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteQuote = createAsyncThunk(
  'quote/deleteQuote',
  async (params: { id: number }, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.DELETE}/${params.id}`;
      const response = await httpClient.delete(url);
      return { id: params.id, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to delete quote');
      return rejectWithValue(errorMessage);
    }
  }
);

export const closeQuote = createAsyncThunk(
  'quote/closeQuote',
  async (params: QuoteCloseCreateParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.CLOSE}/${params.id}/close`;
      const response = await httpClient.post(url);
      return { id: params.id, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to close quote');
      return rejectWithValue(errorMessage);
    }
  }
);

export const sendQuoteMail = createAsyncThunk(
  'quote/sendQuoteMail',
  async (params: QuoteSendMailCreateParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.SEND_MAIL}/${params.id}/send-mail`;
      const response = await httpClient.post(url);
      return { id: params.id, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to send quote mail');
      return rejectWithValue(errorMessage);
    }
  }
);

export const sendQuoteMailFromPORequest = createAsyncThunk(
  'quote/sendQuoteMailFromPORequest',
  async ({ id, data }: { id: number; data: SendQuoteMailFromPORequestDto }, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.SEND_MAIL}/${id}/send-mail-from-po-request`;
      const response = await httpClient.post(url, data);
      return { id, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to send quote mail from PO request');
      return rejectWithValue(errorMessage);
    }
  }
);

// New async thunks for additional quote operations
export const setQuoteItemAwarded = createAsyncThunk(
  'quote/setQuoteItemAwarded',
  async (params: QuoteItemSetAwardedPartialUpdateParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.ITEM_SET_AWARDED}/${params.id}/set-awarded`;
      const response = await httpClient.patch(url);
      return { id: params.id, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to set quote item as awarded');
      return rejectWithValue(errorMessage);
    }
  }
);

export const setQuoteItemRejected = createAsyncThunk(
  'quote/setQuoteItemRejected',
  async (params: QuoteItemSetRejectedPartialUpdateParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.ITEM_SET_REJECTED}/${params.id}/set-rejected`;
      const response = await httpClient.patch(url);
      return { id: params.id, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to set quote item as rejected');
      return rejectWithValue(errorMessage);
    }
  }
);

export const setQuoteClosed = createAsyncThunk(
  'quote/setQuoteClosed',
  async (params: QuoteSetClosedPartialUpdateParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.SET_CLOSED}/${params.id}/set-closed`;
      const response = await httpClient.patch(url);
      return { id: params.id, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to set quote as closed');
      return rejectWithValue(errorMessage);
    }
  }
);

export const setQuoteResponded = createAsyncThunk(
  'quote/setQuoteResponded',
  async (params: QuoteSetRespondedPartialUpdateParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.SET_RESPONDED}/${params.id}/set-responded`;
      const response = await httpClient.patch(url);
      return { id: params.id, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to set quote as responded');
      return rejectWithValue(errorMessage);
    }
  }
);

export const setQuoteViewed = createAsyncThunk(
  'quote/setQuoteViewed',
  async (params: QuoteSetViewedPartialUpdateParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.SET_VIEWED}/${params.id}/set-viewed`;
      const response = await httpClient.patch(url);
      return { id: params.id, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to set quote as viewed');
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchQuoteReviewDetail = createAsyncThunk(
  'quote/fetchQuoteReviewDetail',
  async (params: QuotePublicDetailParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.REVIEW_DETAIL}/${params.publicId}`;
      const response = await httpClient.get(url);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to fetch quote review details');
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateQuoteReviewItems = createAsyncThunk(
  'quote/updateQuoteReviewItems',
  async ({ publicId, data }: { publicId: string; data: UpdateSingleQuoteItemDto }, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.REVIEW_DETAIL}/${publicId}/items`;
      const response = await httpClient.patch(url, data);
      return { publicId, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to update quote review items');
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateQuoteReview = createAsyncThunk(
  'quote/updateQuoteReview',
  async ({ publicId, data }: { publicId: string; data: UpdateQuoteDto }, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.REVIEW_DETAIL}/${publicId}`;
      const response = await httpClient.patch(url, data);
      return { publicId, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to update quote review');
      return rejectWithValue(errorMessage);
    }
  }
);

// New endpoint: Quote Negotiate
export const negotiateQuoteItem = createAsyncThunk(
  'quote/negotiateQuoteItem',
  async ({ quoteItemId, data }: { quoteItemId: number; data: SetQuoteItemNegotiateDto }, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.ITEM_NEGOTIATE}/${quoteItemId}/negotiate`;
      const response = await httpClient.patch(url, data);
      return { quoteItemId, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to negotiate quote item');
      return rejectWithValue(errorMessage);
    }
  }
);

// New endpoint: Quote Public Items Reject
export const rejectQuotePublicItem = createAsyncThunk(
  'quote/rejectQuotePublicItem',
  async ({ publicId, quoteItemId }: { publicId: string; quoteItemId: number }, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE_PUBLIC.REVIEW_ITEMS_UPDATE}/${publicId}/items/${quoteItemId}/reject`;
      const response = await httpClient.patch(url);
      return { publicId, quoteItemId, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to reject quote public item');
      return rejectWithValue(errorMessage);
    }
  }
);

// New PO Request Quote endpoints
export const fetchPoRequestQuoteItems = createAsyncThunk(
  'quote/fetchPoRequestQuoteItems',
  async (params: PoRequestQuoteItemsListParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.PO_REQUEST_QUOTE.QUOTE_ITEMS}/${params.poRequestId}/quote-items`;
      const response = await httpClient.get(url);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to fetch PO request quote items');
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchNegotiationSuppliers = createAsyncThunk(
  'quote/fetchNegotiationSuppliers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await httpClient.get(API_ENDPOINTS.PO_REQUEST_QUOTE.NEGOTIATION_SUPPLIERS);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to fetch negotiation suppliers');
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchAwardedSuppliers = createAsyncThunk(
  'quote/fetchAwardedSuppliers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await httpClient.get(API_ENDPOINTS.PO_REQUEST_QUOTE.AWARDED_SUPPLIERS);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to fetch awarded suppliers');
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchSupplierAwardedQuoteItems = createAsyncThunk(
  'quote/fetchSupplierAwardedQuoteItems',
  async (params: PoRequestSupplierAwardedQuoteItemsListParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.PO_REQUEST_QUOTE.SUPPLIER_AWARDED_QUOTE_ITEMS}/${params.supplierId}/awarded-quote-items`;
      const response = await httpClient.get(url);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to fetch supplier awarded quote items');
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchSupplierNegotiationQuoteItems = createAsyncThunk(
  'quote/fetchSupplierNegotiationQuoteItems',
  async (params: PoRequestSupplierNegotiationQuoteItemsListParams, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.PO_REQUEST_QUOTE.SUPPLIER_NEGOTIATION_QUOTE_ITEMS}/${params.supplierId}/negotiation-quote-items`;
      const response = await httpClient.get(url);
      return response;
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to fetch supplier negotiation quote items');
      return rejectWithValue(errorMessage);
    }
  }
);

// Fetch Quote Item History
export const fetchQuoteItemHistory = createAsyncThunk(
  'quote/fetchQuoteItemHistory',
  async (quoteItemId: number, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.ITEM_HISTORY}/${quoteItemId}/history`;
      const response = await httpClient.get(url);
      return { quoteItemId, data: response.data || response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Failed to fetch quote item history');
      return rejectWithValue(errorMessage);
    }
  }
);

/** POST /api/Quote/{quoteId}/checkout */
export const tryToCheckout = createAsyncThunk(
  'quote/tryToCheckout',
  async (quoteId: number, { rejectWithValue }) => {
    try {
      const url = `${API_ENDPOINTS.QUOTE.LIST}/${quoteId}/checkout`;
      const response = await httpClient.post(url);
      return { quoteId, response };
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Teklif tetiği çekme işlemi başarısız');
      return rejectWithValue(errorMessage);
    }
  }
);

// Quote slice
const quoteSlice = createSlice({
  name: 'quote',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = {
        list: null,
        detail: null,
        create: null,
        update: null,
        updateItems: null,
        delete: null,
        close: null,
        sendMail: null,
        fromPoRequests: null,
        fromQuoteItems: null,
        setAwarded: null,
        setRejected: null,
        setClosed: null,
        setResponded: null,
        setViewed: null,
        reviewDetail: null,
        reviewItemsUpdate: null,
        reviewUpdate: null,
        negotiate: null,
        rejectPublicItem: null,
        bulk: null,
        poRequestQuoteItems: null,
        negotiationSuppliers: null,
        awardedSuppliers: null,
        supplierAwardedQuoteItems: null,
        supplierNegotiationQuoteItems: null,
        quoteItemHistory: null,
        checkout: null,
      };
    },
    clearCurrentQuote: (state) => {
      state.currentQuote = null;
      state.quoteItems = [];
      state.poRequestQuoteItems = [];
      state.negotiationSuppliers = [];
      state.awardedSuppliers = [];
      state.supplierAwardedQuoteItems = [];
      state.supplierNegotiationQuoteItems = [];
      state.quoteItemHistory = [];
    },
    setFilters: (state, action: PayloadAction<QuoteListParams>) => {
      state.filters = action.payload;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number; total: number }>) => {
      state.pagination = action.payload;
    },
    resetQuoteState: () => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    // Fetch quotes
    builder
      .addCase(fetchQuotes.pending, (state) => {
        state.loading.list = true;
        state.error.list = null;
      })
      .addCase(fetchQuotes.fulfilled, (state, action) => {
        state.loading.list = false;
        state.quotes = action.payload.data || [];
        // Note: total count might not be available in the response structure
        // state.pagination.total = action.payload.total || 0;
      })
      .addCase(fetchQuotes.rejected, (state, action) => {
        state.loading.list = false;
        state.error.list = action.payload as string;
      });

    // Fetch quote detail
    builder
      .addCase(fetchQuoteDetail.pending, (state) => {
        state.loading.detail = true;
        state.error.detail = null;
      })
      .addCase(fetchQuoteDetail.fulfilled, (state, action) => {
        state.loading.detail = false;
        state.currentQuote = action.payload.data;
        state.quoteItems = action.payload.data?.quoteItems || [];
      })
      .addCase(fetchQuoteDetail.rejected, (state, action) => {
        state.loading.detail = false;
        state.error.detail = action.payload as string;
      });

    // Create quote
    builder
      .addCase(createQuote.pending, (state) => {
        state.loading.create = true;
        state.error.create = null;
      })
      .addCase(createQuote.fulfilled, (state, action) => {
        state.loading.create = false;
        if (action.payload.data) {
          state.quotes.unshift(action.payload.data);
        }
      })
      .addCase(createQuote.rejected, (state, action) => {
        state.loading.create = false;
        state.error.create = action.payload as string;
      });

    // Create quote from PO requests
    builder
      .addCase(createQuoteFromPoRequests.pending, (state) => {
        state.loading.fromPoRequests = true;
        state.error.fromPoRequests = null;
      })
      .addCase(createQuoteFromPoRequests.fulfilled, (state, action) => {
        state.loading.fromPoRequests = false;
        if (action.payload.data) {
          state.quotes.unshift(action.payload.data);
        }
      })
      .addCase(createQuoteFromPoRequests.rejected, (state, action) => {
        state.loading.fromPoRequests = false;
        state.error.fromPoRequests = action.payload as string;
      });

    // Create quote from quote items
    builder
      .addCase(createQuoteFromQuoteItems.pending, (state) => {
        state.loading.fromQuoteItems = true;
        state.error.fromQuoteItems = null;
      })
      .addCase(createQuoteFromQuoteItems.fulfilled, (state, action) => {
        state.loading.fromQuoteItems = false;
        if (action.payload.data) {
          state.quotes.unshift(action.payload.data);
        }
      })
      .addCase(createQuoteFromQuoteItems.rejected, (state, action) => {
        state.loading.fromQuoteItems = false;
        state.error.fromQuoteItems = action.payload as string;
      });

    // Update quote
    builder
      .addCase(updateQuote.pending, (state) => {
        state.loading.update = true;
        state.error.update = null;
      })
      .addCase(updateQuote.fulfilled, (state, action) => {
        state.loading.update = false;
        if (action.payload.data) {
          const index = state.quotes.findIndex(quote => quote.id === action.payload.data.id);
          if (index !== -1) {
            state.quotes[index] = action.payload.data;
          }
          if (state.currentQuote?.id === action.payload.data.id) {
            state.currentQuote = action.payload.data;
          }
        }
      })
      .addCase(updateQuote.rejected, (state, action) => {
        state.loading.update = false;
        state.error.update = action.payload as string;
      });

    // Update quote items
    builder
      .addCase(updateQuoteItems.pending, (state) => {
        state.loading.updateItems = true;
        state.error.updateItems = null;
      })
      .addCase(updateQuoteItems.fulfilled, (state, action) => {
        state.loading.updateItems = false;
        // Update quote items in the store
        const quoteIndex = state.quotes.findIndex(quote => quote.id === action.payload.quoteId);
        if (quoteIndex !== -1 && state.quotes[quoteIndex]) {
          // Refresh the quote data after updating items
          // Note: In a real implementation, you might want to refetch the quote detail
        }
        if (state.currentQuote?.id === action.payload.quoteId) {
          // Refresh current quote data
          // Note: In a real implementation, you might want to refetch the quote detail
        }
      })
      .addCase(updateQuoteItems.rejected, (state, action) => {
        state.loading.updateItems = false;
        state.error.updateItems = action.payload as string;
      });

    // Delete quote
    builder
      .addCase(deleteQuote.pending, (state) => {
        state.loading.delete = true;
        state.error.delete = null;
      })
      .addCase(deleteQuote.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.quotes = state.quotes.filter(quote => quote.id !== action.payload.id);
        if (state.currentQuote?.id === action.payload.id) {
          state.currentQuote = null;
          state.quoteItems = [];
        }
      })
      .addCase(deleteQuote.rejected, (state, action) => {
        state.loading.delete = false;
        state.error.delete = action.payload as string;
      });

    // Close quote
    builder
      .addCase(closeQuote.pending, (state) => {
        state.loading.close = true;
        state.error.close = null;
      })
      .addCase(closeQuote.fulfilled, (state, action) => {
        state.loading.close = false;
        const index = state.quotes.findIndex(quote => quote.id === action.payload.id);
        if (index !== -1 && state.quotes[index]) {
          state.quotes[index] = { ...state.quotes[index], quoteStatus: 'Closed' };
        }
        if (state.currentQuote?.id === action.payload.id) {
          state.currentQuote.quoteStatus = 'Closed';
        }
      })
      .addCase(closeQuote.rejected, (state, action) => {
        state.loading.close = false;
        state.error.close = action.payload as string;
      });

    // Tetiği çek
    builder
      .addCase(tryToCheckout.pending, (state) => {
        state.loading.checkout = true;
        state.error.checkout = null;
      })
      .addCase(tryToCheckout.fulfilled, (state) => {
        state.loading.checkout = false;
        state.error.checkout = null;
      })
      .addCase(tryToCheckout.rejected, (state, action) => {
        state.loading.checkout = false;
        state.error.checkout = action.payload as string;
      });

    // Send quote mail
    builder
      .addCase(sendQuoteMail.pending, (state) => {
        state.loading.sendMail = true;
        state.error.sendMail = null;
      })
      .addCase(sendQuoteMail.fulfilled, (state, action) => {
        state.loading.sendMail = false;
        const index = state.quotes.findIndex(quote => quote.id === action.payload.id);
        if (index !== -1 && state.quotes[index]) {
          state.quotes[index] = { 
            ...state.quotes[index], 
            emailSent: true, 
            emailSentAt: new Date() 
          };
        }
        if (state.currentQuote?.id === action.payload.id) {
          state.currentQuote.emailSent = true;
          state.currentQuote.emailSentAt = new Date();
        }
      })
      .addCase(sendQuoteMail.rejected, (state, action) => {
        state.loading.sendMail = false;
        state.error.sendMail = action.payload as string;
      });

    // Send quote mail from PO request
    builder
      .addCase(sendQuoteMailFromPORequest.pending, (state) => {
        state.loading.sendMail = true;
        state.error.sendMail = null;
      })
      .addCase(sendQuoteMailFromPORequest.fulfilled, (state, action) => {
        state.loading.sendMail = false;
        const index = state.quotes.findIndex(quote => quote.id === action.payload.id);
        if (index !== -1 && state.quotes[index]) {
          state.quotes[index] = { 
            ...state.quotes[index], 
            emailSent: true, 
            emailSentAt: new Date() 
          };
        }
        if (state.currentQuote?.id === action.payload.id) {
          state.currentQuote.emailSent = true;
          state.currentQuote.emailSentAt = new Date();
        }
      })
      .addCase(sendQuoteMailFromPORequest.rejected, (state, action) => {
        state.loading.sendMail = false;
        state.error.sendMail = action.payload as string;
      });

    // Set Quote Item Awarded
    builder
      .addCase(setQuoteItemAwarded.pending, (state) => {
        state.loading.setAwarded = true;
        state.error.setAwarded = null;
      })
      .addCase(setQuoteItemAwarded.fulfilled, (state, action) => {
        state.loading.setAwarded = false;
        // Update quote item status in the store
        const quoteIndex = state.quotes.findIndex(quote => 
          quote.quoteItems?.some(item => item.id === action.payload.id)
        );
        if (quoteIndex !== -1) {
          const quote = state.quotes[quoteIndex];
          if (quote?.quoteItems) {
            const itemIndex = quote.quoteItems.findIndex(item => item.id === action.payload.id);
            if (itemIndex !== -1) {
              quote.quoteItems[itemIndex]!.decisionStatus = 'Awarded';
            }
          }
        }
      })
      .addCase(setQuoteItemAwarded.rejected, (state, action) => {
        state.loading.setAwarded = false;
        state.error.setAwarded = action.payload as string;
      });

    // Set Quote Item Rejected
    builder
      .addCase(setQuoteItemRejected.pending, (state) => {
        state.loading.setRejected = true;
        state.error.setRejected = null;
      })
      .addCase(setQuoteItemRejected.fulfilled, (state, action) => {
        state.loading.setRejected = false;
        // Update quote item status in the store
        const quoteIndex = state.quotes.findIndex(quote => 
          quote.quoteItems?.some(item => item.id === action.payload.id)
        );
        if (quoteIndex !== -1) {
          const quote = state.quotes[quoteIndex];
          if (quote?.quoteItems) {
            const itemIndex = quote.quoteItems.findIndex(item => item.id === action.payload.id);
            if (itemIndex !== -1) {
              quote.quoteItems[itemIndex]!.decisionStatus = 'Rejected';
            }
          }
        }
      })
      .addCase(setQuoteItemRejected.rejected, (state, action) => {
        state.loading.setRejected = false;
        state.error.setRejected = action.payload as string;
      });

    // Set Quote Closed
    builder
      .addCase(setQuoteClosed.pending, (state) => {
        state.loading.setClosed = true;
        state.error.setClosed = null;
      })
      .addCase(setQuoteClosed.fulfilled, (state, action) => {
        state.loading.setClosed = false;
        const index = state.quotes.findIndex(quote => quote.id === action.payload.id);
        if (index !== -1 && state.quotes[index]) {
          state.quotes[index]!.quoteStatus = 'Closed';
        }
        if (state.currentQuote?.id === action.payload.id) {
          state.currentQuote.quoteStatus = 'Closed';
        }
      })
      .addCase(setQuoteClosed.rejected, (state, action) => {
        state.loading.setClosed = false;
        state.error.setClosed = action.payload as string;
      });

    // Set Quote Responded
    builder
      .addCase(setQuoteResponded.pending, (state) => {
        state.loading.setResponded = true;
        state.error.setResponded = null;
      })
      .addCase(setQuoteResponded.fulfilled, (state, action) => {
        state.loading.setResponded = false;
        const index = state.quotes.findIndex(quote => quote.id === action.payload.id);
        if (index !== -1 && state.quotes[index]) {
          state.quotes[index]!.responded = true;
          state.quotes[index]!.respondedAt = new Date();
        }
        if (state.currentQuote?.id === action.payload.id) {
          state.currentQuote.responded = true;
          state.currentQuote.respondedAt = new Date();
        }
      })
      .addCase(setQuoteResponded.rejected, (state, action) => {
        state.loading.setResponded = false;
        state.error.setResponded = action.payload as string;
      });

    // Set Quote Viewed
    builder
      .addCase(setQuoteViewed.pending, (state) => {
        state.loading.setViewed = true;
        state.error.setViewed = null;
      })
      .addCase(setQuoteViewed.fulfilled, (state, action) => {
        state.loading.setViewed = false;
        const index = state.quotes.findIndex(quote => quote.id === action.payload.id);
        if (index !== -1 && state.quotes[index]) {
          state.quotes[index]!.emailOpened = true;
          state.quotes[index]!.emailOpenedAt = new Date();
        }
        if (state.currentQuote?.id === action.payload.id) {
          state.currentQuote.emailOpened = true;
          state.currentQuote.emailOpenedAt = new Date();
        }
      })
      .addCase(setQuoteViewed.rejected, (state, action) => {
        state.loading.setViewed = false;
        state.error.setViewed = action.payload as string;
      });

    // Fetch Quote Review Detail
    builder
      .addCase(fetchQuoteReviewDetail.pending, (state) => {
        state.loading.reviewDetail = true;
        state.error.reviewDetail = null;
      })
      .addCase(fetchQuoteReviewDetail.fulfilled, (state, action) => {
        state.loading.reviewDetail = false;
        state.currentQuote = action.payload.data;
        state.quoteItems = action.payload.data?.quoteItems || [];
      })
      .addCase(fetchQuoteReviewDetail.rejected, (state, action) => {
        state.loading.reviewDetail = false;
        state.error.reviewDetail = action.payload as string;
      });

    // Update Quote Review Items
    builder
      .addCase(updateQuoteReviewItems.pending, (state) => {
        state.loading.reviewItemsUpdate = true;
        state.error.reviewItemsUpdate = null;
      })
      .addCase(updateQuoteReviewItems.fulfilled, (state) => {
        state.loading.reviewItemsUpdate = false;
        // Update quote items in the store
        // Note: publicId comparison might need to be adjusted based on actual data structure
        // if (state.currentQuote?.id === action.payload.publicId) {
        //   // Refresh the quote data after updating review items
        //   // Note: In a real implementation, you might want to refetch the quote review detail
        // }
      })
      .addCase(updateQuoteReviewItems.rejected, (state, action) => {
        state.loading.reviewItemsUpdate = false;
        state.error.reviewItemsUpdate = action.payload as string;
      });

    // Update Quote Review
    builder
      .addCase(updateQuoteReview.pending, (state) => {
        state.loading.reviewUpdate = true;
        state.error.reviewUpdate = null;
      })
      .addCase(updateQuoteReview.fulfilled, (state) => {
        state.loading.reviewUpdate = false;
        // Update quote in the store
        // Note: publicId comparison might need to be adjusted based on actual data structure
        // if (state.currentQuote?.id === action.payload.publicId) {
        //   // Refresh the quote data after updating review
        //   // Note: In a real implementation, you might want to refetch the quote review detail
        // }
      })
      .addCase(updateQuoteReview.rejected, (state, action) => {
        state.loading.reviewUpdate = false;
        state.error.reviewUpdate = action.payload as string;
      });

    // Negotiate Quote Item
    builder
      .addCase(negotiateQuoteItem.pending, (state) => {
        state.loading.negotiate = true;
        state.error.negotiate = null;
      })
      .addCase(negotiateQuoteItem.fulfilled, (state, action) => {
        state.loading.negotiate = false;
        // Update quote item in the store
        const quoteIndex = state.quotes.findIndex(quote => 
          quote.quoteItems?.some(item => item.id === action.payload.quoteItemId)
        );
        if (quoteIndex !== -1) {
          const quote = state.quotes[quoteIndex];
          if (quote?.quoteItems) {
            const itemIndex = quote.quoteItems.findIndex(item => item.id === action.payload.quoteItemId);
            if (itemIndex !== -1) {
              // Update the item with negotiation data
              // Note: The actual update would depend on the response structure
            }
          }
        }
      })
      .addCase(negotiateQuoteItem.rejected, (state, action) => {
        state.loading.negotiate = false;
        state.error.negotiate = action.payload as string;
      });

    // Reject Quote Public Item
    builder
      .addCase(rejectQuotePublicItem.pending, (state) => {
        state.loading.rejectPublicItem = true;
        state.error.rejectPublicItem = null;
      })
      .addCase(rejectQuotePublicItem.fulfilled, (state, action) => {
        state.loading.rejectPublicItem = false;
        // Update quote item status in the store
        // Note: This would update the current quote if it matches the publicId
        if (state.currentQuote && state.currentQuote.quoteItems) {
          const itemIndex = state.currentQuote.quoteItems.findIndex(item => item.id === action.payload.quoteItemId);
          if (itemIndex !== -1) {
            state.currentQuote.quoteItems[itemIndex]!.decisionStatus = 'Rejected';
          }
        }
      })
      .addCase(rejectQuotePublicItem.rejected, (state, action) => {
        state.loading.rejectPublicItem = false;
        state.error.rejectPublicItem = action.payload as string;
      });

    // Fetch PO Request Quote Items
    builder
      .addCase(fetchPoRequestQuoteItems.pending, (state) => {
        state.loading.poRequestQuoteItems = true;
        state.error.poRequestQuoteItems = null;
      })
      .addCase(fetchPoRequestQuoteItems.fulfilled, (state, action) => {
        state.loading.poRequestQuoteItems = false;
        state.poRequestQuoteItems = action.payload.data || [];
      })
      .addCase(fetchPoRequestQuoteItems.rejected, (state, action) => {
        state.loading.poRequestQuoteItems = false;
        state.error.poRequestQuoteItems = action.payload as string;
      });

    // Fetch Negotiation Suppliers
    builder
      .addCase(fetchNegotiationSuppliers.pending, (state) => {
        state.loading.negotiationSuppliers = true;
        state.error.negotiationSuppliers = null;
      })
      .addCase(fetchNegotiationSuppliers.fulfilled, (state, action) => {
        state.loading.negotiationSuppliers = false;
        state.negotiationSuppliers = action.payload.data || [];
      })
      .addCase(fetchNegotiationSuppliers.rejected, (state, action) => {
        state.loading.negotiationSuppliers = false;
        state.error.negotiationSuppliers = action.payload as string;
      });

    // Fetch Awarded Suppliers
    builder
      .addCase(fetchAwardedSuppliers.pending, (state) => {
        state.loading.awardedSuppliers = true;
        state.error.awardedSuppliers = null;
      })
      .addCase(fetchAwardedSuppliers.fulfilled, (state, action) => {
        state.loading.awardedSuppliers = false;
        state.awardedSuppliers = action.payload.data || [];
      })
      .addCase(fetchAwardedSuppliers.rejected, (state, action) => {
        state.loading.awardedSuppliers = false;
        state.error.awardedSuppliers = action.payload as string;
      });

    // Fetch Supplier Awarded Quote Items
    builder
      .addCase(fetchSupplierAwardedQuoteItems.pending, (state) => {
        state.loading.supplierAwardedQuoteItems = true;
        state.error.supplierAwardedQuoteItems = null;
      })
      .addCase(fetchSupplierAwardedQuoteItems.fulfilled, (state, action) => {
        state.loading.supplierAwardedQuoteItems = false;
        state.supplierAwardedQuoteItems = action.payload.data || [];
      })
      .addCase(fetchSupplierAwardedQuoteItems.rejected, (state, action) => {
        state.loading.supplierAwardedQuoteItems = false;
        state.error.supplierAwardedQuoteItems = action.payload as string;
      });

    // Fetch Supplier Negotiation Quote Items
    builder
      .addCase(fetchSupplierNegotiationQuoteItems.pending, (state) => {
        state.loading.supplierNegotiationQuoteItems = true;
        state.error.supplierNegotiationQuoteItems = null;
      })
      .addCase(fetchSupplierNegotiationQuoteItems.fulfilled, (state, action) => {
        state.loading.supplierNegotiationQuoteItems = false;
        state.supplierNegotiationQuoteItems = action.payload.data || [];
      })
      .addCase(fetchSupplierNegotiationQuoteItems.rejected, (state, action) => {
        state.loading.supplierNegotiationQuoteItems = false;
        state.error.supplierNegotiationQuoteItems = action.payload as string;
      });

    // Fetch Quote Item History
    builder
      .addCase(fetchQuoteItemHistory.pending, (state) => {
        state.loading.quoteItemHistory = true;
        state.error.quoteItemHistory = null;
      })
      .addCase(fetchQuoteItemHistory.fulfilled, (state, action) => {
        state.loading.quoteItemHistory = false;
        state.quoteItemHistory = Array.isArray(action.payload.data) 
          ? action.payload.data 
          : action.payload.data?.data || [];
      })
      .addCase(fetchQuoteItemHistory.rejected, (state, action) => {
        state.loading.quoteItemHistory = false;
        state.error.quoteItemHistory = action.payload as string;
      });
  },
});

// Export actions
export const {
  clearErrors,
  clearCurrentQuote,
  setFilters,
  setPagination,
  resetQuoteState,
} = quoteSlice.actions;

// Export reducer
export default quoteSlice.reducer;

// Selectors
export const selectQuotes = (state: { quote: QuoteState }) => state.quote.quotes;
export const selectCurrentQuote = (state: { quote: QuoteState }) => state.quote.currentQuote;
export const selectQuoteItems = (state: { quote: QuoteState }) => state.quote.quoteItems;
export const selectPoRequestQuoteItems = (state: { quote: QuoteState }) => state.quote.poRequestQuoteItems;
export const selectNegotiationSuppliers = (state: { quote: QuoteState }) => state.quote.negotiationSuppliers;
export const selectAwardedSuppliers = (state: { quote: QuoteState }) => state.quote.awardedSuppliers;
export const selectSupplierAwardedQuoteItems = (state: { quote: QuoteState }) => state.quote.supplierAwardedQuoteItems;
export const selectSupplierNegotiationQuoteItems = (state: { quote: QuoteState }) => state.quote.supplierNegotiationQuoteItems;
export const selectQuoteItemHistory = (state: { quote: QuoteState }) => state.quote.quoteItemHistory;
export const selectQuoteLoading = (state: { quote: QuoteState }) => state.quote.loading;
export const selectQuoteError = (state: { quote: QuoteState }) => state.quote.error;
export const selectQuoteFilters = (state: { quote: QuoteState }) => state.quote.filters;
export const selectQuotePagination = (state: { quote: QuoteState }) => state.quote.pagination;

// Loading selectors
export const selectIsQuoteListLoading = (state: { quote: QuoteState }) => state.quote.loading.list;
export const selectIsQuoteDetailLoading = (state: { quote: QuoteState }) => state.quote.loading.detail;
export const selectIsQuoteCreating = (state: { quote: QuoteState }) => state.quote.loading.create;
export const selectIsQuoteUpdating = (state: { quote: QuoteState }) => state.quote.loading.update;
export const selectIsQuoteItemsUpdating = (state: { quote: QuoteState }) => state.quote.loading.updateItems;
export const selectIsQuoteDeleting = (state: { quote: QuoteState }) => state.quote.loading.delete;
export const selectIsQuoteClosing = (state: { quote: QuoteState }) => state.quote.loading.close;
export const selectIsQuoteSendingMail = (state: { quote: QuoteState }) => state.quote.loading.sendMail;
export const selectIsQuoteFromPoRequestsLoading = (state: { quote: QuoteState }) => state.quote.loading.fromPoRequests;
export const selectIsQuoteItemAwardedLoading = (state: { quote: QuoteState }) => state.quote.loading.setAwarded;
export const selectIsQuoteItemRejectedLoading = (state: { quote: QuoteState }) => state.quote.loading.setRejected;
export const selectIsQuoteClosedLoading = (state: { quote: QuoteState }) => state.quote.loading.setClosed;
export const selectIsQuoteRespondedLoading = (state: { quote: QuoteState }) => state.quote.loading.setResponded;
export const selectIsQuoteViewedLoading = (state: { quote: QuoteState }) => state.quote.loading.setViewed;
export const selectIsQuoteReviewDetailLoading = (state: { quote: QuoteState }) => state.quote.loading.reviewDetail;
export const selectIsQuoteReviewItemsUpdateLoading = (state: { quote: QuoteState }) => state.quote.loading.reviewItemsUpdate;
export const selectIsQuoteReviewUpdateLoading = (state: { quote: QuoteState }) => state.quote.loading.reviewUpdate;
export const selectIsQuoteNegotiateLoading = (state: { quote: QuoteState }) => state.quote.loading.negotiate;
export const selectIsQuoteRejectPublicItemLoading = (state: { quote: QuoteState }) => state.quote.loading.rejectPublicItem;
export const selectIsQuoteBulkLoading = (state: { quote: QuoteState }) => state.quote.loading.bulk;
export const selectIsQuoteFromQuoteItemsLoading = (state: { quote: QuoteState }) => state.quote.loading.fromQuoteItems;
export const selectIsPoRequestQuoteItemsLoading = (state: { quote: QuoteState }) => state.quote.loading.poRequestQuoteItems;
export const selectIsNegotiationSuppliersLoading = (state: { quote: QuoteState }) => state.quote.loading.negotiationSuppliers;
export const selectIsAwardedSuppliersLoading = (state: { quote: QuoteState }) => state.quote.loading.awardedSuppliers;
export const selectIsSupplierAwardedQuoteItemsLoading = (state: { quote: QuoteState }) => state.quote.loading.supplierAwardedQuoteItems;
export const selectIsSupplierNegotiationQuoteItemsLoading = (state: { quote: QuoteState }) => state.quote.loading.supplierNegotiationQuoteItems;
export const selectIsQuoteItemHistoryLoading = (state: { quote: QuoteState }) => state.quote.loading.quoteItemHistory;

// Error selectors
export const selectQuoteListError = (state: { quote: QuoteState }) => state.quote.error.list;
export const selectQuoteDetailError = (state: { quote: QuoteState }) => state.quote.error.detail;
export const selectQuoteCreateError = (state: { quote: QuoteState }) => state.quote.error.create;
export const selectQuoteUpdateError = (state: { quote: QuoteState }) => state.quote.error.update;
export const selectQuoteUpdateItemsError = (state: { quote: QuoteState }) => state.quote.error.updateItems;
export const selectQuoteDeleteError = (state: { quote: QuoteState }) => state.quote.error.delete;
export const selectQuoteCloseError = (state: { quote: QuoteState }) => state.quote.error.close;
export const selectQuoteSendMailError = (state: { quote: QuoteState }) => state.quote.error.sendMail;
export const selectQuoteFromPoRequestsError = (state: { quote: QuoteState }) => state.quote.error.fromPoRequests;
export const selectQuoteItemAwardedError = (state: { quote: QuoteState }) => state.quote.error.setAwarded;
export const selectQuoteItemRejectedError = (state: { quote: QuoteState }) => state.quote.error.setRejected;
export const selectQuoteClosedError = (state: { quote: QuoteState }) => state.quote.error.setClosed;
export const selectQuoteRespondedError = (state: { quote: QuoteState }) => state.quote.error.setResponded;
export const selectQuoteViewedError = (state: { quote: QuoteState }) => state.quote.error.setViewed;
export const selectQuoteReviewDetailError = (state: { quote: QuoteState }) => state.quote.error.reviewDetail;
export const selectQuoteReviewItemsUpdateError = (state: { quote: QuoteState }) => state.quote.error.reviewItemsUpdate;
export const selectQuoteReviewUpdateError = (state: { quote: QuoteState }) => state.quote.error.reviewUpdate;
export const selectQuoteNegotiateError = (state: { quote: QuoteState }) => state.quote.error.negotiate;
export const selectQuoteRejectPublicItemError = (state: { quote: QuoteState }) => state.quote.error.rejectPublicItem;
export const selectQuoteBulkError = (state: { quote: QuoteState }) => state.quote.error.bulk;
export const selectQuoteFromQuoteItemsError = (state: { quote: QuoteState }) => state.quote.error.fromQuoteItems;
export const selectPoRequestQuoteItemsError = (state: { quote: QuoteState }) => state.quote.error.poRequestQuoteItems;
export const selectNegotiationSuppliersError = (state: { quote: QuoteState }) => state.quote.error.negotiationSuppliers;
export const selectAwardedSuppliersError = (state: { quote: QuoteState }) => state.quote.error.awardedSuppliers;
export const selectSupplierAwardedQuoteItemsError = (state: { quote: QuoteState }) => state.quote.error.supplierAwardedQuoteItems;
export const selectSupplierNegotiationQuoteItemsError = (state: { quote: QuoteState }) => state.quote.error.supplierNegotiationQuoteItems;
export const selectQuoteItemHistoryError = (state: { quote: QuoteState }) => state.quote.error.quoteItemHistory;
